import { useCallback, useEffect, useMemo } from 'react'

import { CursorState } from '@editor/config/constants/cursor'
import { EditorConfig } from '@editor/interfaces/editor'
import { BlueprintEditorTools } from '@editor/pages/projects/blueprint-editor/definitions'
import { RootState, useAppDispatch } from '@editor/store/app'
import { setCursor, setWorkingElementId } from '@editor/store/tool'
import { useSelector } from 'react-redux'

import { reset } from '../store'
import useUndo from './useUndo'

export default function useEditor(): EditorConfig {
  // Store
  const dispatch = useAppDispatch()
  const selectedTool = useSelector((state: RootState) => state.blueprint.editor.selectedTool)
  const prevSelectedTool = useSelector((state: RootState) => state.blueprint.editor.prevSelectedTool)

  // Derived state
  const isToolSelected = useMemo(() => selectedTool === BlueprintEditorTools.Circle, [selectedTool])
  const isPreviousTool = useMemo(() => prevSelectedTool === BlueprintEditorTools.Circle, [prevSelectedTool])

  // Hooks
  const { canUndo } = useUndo()

  /**
   * When user changes tools (in or out of Polyline tool), update cursor.
   */
  useEffect(() => {
    if (isToolSelected) {
      dispatch(setCursor(CursorState.CROSSHAIR))
    } else if (isPreviousTool && selectedTool !== BlueprintEditorTools.Focus) {
      dispatch(setCursor(CursorState.DEFAULT))
      dispatch(reset())
      dispatch(setWorkingElementId(undefined))
    }
  }, [isToolSelected, selectedTool, isPreviousTool, dispatch])

  /**
   * Reset on unmount
   */
  useEffect(
    () => () => {
      dispatch(reset())
    },
    [dispatch],
  )

  return {
    buttons: {
      undo: {
        isShown: useCallback(() => isToolSelected, [isToolSelected]),
        isDisabled: useCallback(() => !canUndo, [canUndo]),
      },
    },
  }
}
